import React, { useState, useEffect } from "react";
import { Drawer, Button, Tag, Icon, Tooltip } from "antd";
import moment from "moment";
import styled from "styled-components";

import { toBRL } from "~/components/UI/InputCurrency";

var HeaderCard = styled.div`
    background: linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%);
    border: 1px solid #d9d9d9;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
`;

var HeaderTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

var HeaderTitleText = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.85);
`;

var HeaderSubtitle = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 2px;
`;

var StatsRow = styled.div`
    display: flex;
    gap: 24px;
`;

var StatItem = styled.div`
    flex: 1;
    text-align: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 6px;
`;

var StatValue = styled.div`
    font-size: 24px;
    font-weight: 700;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    line-height: 1.2;
`;

var StatLabel = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 4px;
`;

var ItemCard = styled.div`
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
    position: relative;
    transition: all 0.2s ease;

    &:hover {
        border-color: #d9d9d9;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    }

    &:last-child {
        margin-bottom: 0;
    }
`;

var ItemHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px dashed #e8e8e8;
`;

var ItemDate = styled.div`
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    gap: 4px;
`;

var ItemContent = styled.div`
    display: flex;
    align-items: stretch;
    gap: 12px;
    overflow: hidden;
`;

var ItemSide = styled.div`
    flex: 1 1 0;
    min-width: 0;
    max-width: calc(50% - 32px);
    padding: 12px;
    border-radius: 6px;
    background: ${function(props) { return props.type === 'statement' ? '#fafafa' : '#f0f7ff'; }};
    border-left: 3px solid ${function(props) { return props.type === 'statement' ? '#52c41a' : '#1890ff'; }};
    overflow: hidden;
`;

var ItemSideLabel = styled.div`
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.45);
    margin-bottom: 6px;
`;

var ItemSideValue = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: ${function(props) { return props.color || 'rgba(0, 0, 0, 0.85)'; }};
    margin-bottom: 4px;
    white-space: nowrap;
`;

var ItemSideDescription = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
`;

var ItemArrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex-shrink: 0;
`;

var RemoveButton = styled.div`
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);
    padding: 4px 8px;
    border-radius: 4px;
    transition: all 0.2s;
    font-size: 12px;
    margin-left: auto;

    &:hover {
        color: #ff4d4f;
        background: #fff1f0;
    }
`;

var DrawerFooter = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

var FooterInfo = styled.div`
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
`;

var FooterActions = styled.div`
    display: flex;
    gap: 8px;
`;

var EmptyState = styled.div`
    text-align: center;
    padding: 60px 40px;
    color: rgba(0, 0, 0, 0.45);
`;

var EmptyIcon = styled.div`
    font-size: 64px;
    margin-bottom: 16px;
    color: #d9d9d9;
`;

var EmptyTitle = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.65);
    margin-bottom: 8px;
`;

var EmptyDescription = styled.div`
    font-size: 14px;
`;

var ListContainer = styled.div`
    max-height: calc(100vh - 380px);
    overflow-y: auto;
    padding-right: 4px;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f0f0f0;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #d9d9d9;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #bfbfbf;
    }
`;

// Helper function to normalize strings for comparison
var normalizeString = function(str) {
    if (!str) return '';
    return str
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

// Helper function to check if descriptions are similar
var checkDescriptionSimilarity = function(bankDesc, systemDesc) {
    var normalizedBank = normalizeString(bankDesc);
    var normalizedSystem = normalizeString(systemDesc);

    if (!normalizedBank || !normalizedSystem) {
        return { similar: true, reason: '' };
    }

    if (normalizedBank.indexOf(normalizedSystem) > -1 || normalizedSystem.indexOf(normalizedBank) > -1) {
        return { similar: true, reason: '' };
    }

    var bankWords = normalizedBank.split(' ').filter(function(w) { return w.length > 2; });
    var systemWords = normalizedSystem.split(' ').filter(function(w) { return w.length > 2; });

    var matchingWords = 0;
    for (var i = 0; i < systemWords.length; i++) {
        for (var j = 0; j < bankWords.length; j++) {
            if (bankWords[j].indexOf(systemWords[i]) > -1 || systemWords[i].indexOf(bankWords[j]) > -1) {
                matchingWords++;
                break;
            }
        }
    }

    var similarity = systemWords.length > 0 ? matchingWords / systemWords.length : 0;

    if (similarity < 0.3) {
        return {
            similar: false,
            reason: 'Descrição do extrato diferente do cliente no sistema'
        };
    }

    return { similar: true, reason: '' };
};

var AutoReconcileDrawer = function(props) {
    var visible = props.visible;
    var onClose = props.onClose;
    var onConfirm = props.onConfirm;
    var matchingItems = props.matchingItems || [];
    var isLoading = props.isLoading;

    var [excludedIds, setExcludedIds] = useState([]);

    useEffect(function() {
        if (visible) {
            setExcludedIds([]);
        }
    }, [visible, matchingItems]);

    var getTypeLabel = function(type) {
        if (!type) return { label: 'Outro', color: 'default' };
        if (type.indexOf('CustomerHonorary') > -1 || type === 'mensalidade') {
            return { label: 'Mensalidade', color: 'blue' };
        } else if (type.indexOf('CustomerExtra') > -1 || type === 'extra') {
            return { label: 'Extra', color: 'purple' };
        } else if (type.indexOf('Refund') > -1 || type === 'comissao') {
            return { label: 'Comissão', color: 'orange' };
        }
        return { label: 'Outro', color: 'default' };
    };

    var handleRemoveItem = function(itemId) {
        setExcludedIds(function(prev) {
            return prev.concat([itemId]);
        });
    };

    var activeItems = matchingItems.filter(function(item) {
        return excludedIds.indexOf(item.id) === -1;
    });

    var totalAmount = activeItems.reduce(function(sum, item) {
        return sum + (item.amount || 0);
    }, 0);

    var itemsWithWarning = activeItems.filter(function(item) {
        if (!item.exact_match) return false;
        var similarity = checkDescriptionSimilarity(item.description, item.exact_match.customer_name);
        return !similarity.similar;
    }).length;

    var handleConfirm = function() {
        onConfirm(activeItems);
    };

    var renderItem = function(record, index) {
        var exactMatch = record.exact_match;
        if (!exactMatch) return null;

        var typeInfo = getTypeLabel(exactMatch.type || exactMatch.reconcilable_type);
        var similarity = checkDescriptionSimilarity(record.description, exactMatch.customer_name);

        return (
            <ItemCard key={record.id || index}>
                <ItemHeader>
                    <ItemDate>
                        <Icon type="calendar" />
                        {moment(record.transaction_date).format('DD/MM/YYYY')}
                    </ItemDate>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag color={typeInfo.color} style={{ margin: 0 }}>{typeInfo.label}</Tag>
                        <RemoveButton onClick={function() { handleRemoveItem(record.id); }}>
                            <Icon type="close" /> Remover
                        </RemoveButton>
                    </div>
                </ItemHeader>

                <ItemContent>
                    <ItemSide type="statement">
                        <ItemSideLabel>Extrato Bancário</ItemSideLabel>
                        <ItemSideValue color={record.transaction_type === 'credit' ? '#52c41a' : '#f5222d'}>
                            {record.transaction_type === 'credit' ? '+' : '-'}{toBRL(record.amount)}
                        </ItemSideValue>
                        <Tooltip title={record.description}>
                            <ItemSideDescription>{record.description || '-'}</ItemSideDescription>
                        </Tooltip>
                    </ItemSide>

                    <ItemArrow>
                        {!similarity.similar ? (
                            <Tooltip title={similarity.reason}>
                                <div style={{ textAlign: 'center' }}>
                                    <Icon type="warning" style={{ fontSize: 24, color: '#fa8c16' }} />
                                    <div style={{ fontSize: 10, color: '#fa8c16', marginTop: 2 }}>Atenção</div>
                                </div>
                            </Tooltip>
                        ) : (
                            <Icon type="swap-right" style={{ fontSize: 24, color: '#52c41a' }} />
                        )}
                    </ItemArrow>

                    <ItemSide type="match">
                        <ItemSideLabel>Registro no Sistema</ItemSideLabel>
                        <ItemSideValue color="#1890ff">
                            {toBRL(exactMatch.amount)}
                        </ItemSideValue>
                        <Tooltip title={exactMatch.customer_name}>
                            <ItemSideDescription>{exactMatch.customer_name || '-'}</ItemSideDescription>
                        </Tooltip>
                        {/* ME#### / #SOC### — e por ele que o recebimento amarra na conta a receber */}
                        {exactMatch.cod && (
                            <div style={{
                                marginTop: 4, display: 'inline-block',
                                fontSize: 11, fontWeight: 600, color: '#595959', background: '#f0f0f0',
                                border: '1px solid #d9d9d9', borderRadius: 4, padding: '0 5px',
                                letterSpacing: '0.02em',
                            }}>{exactMatch.cod}</div>
                        )}
                    </ItemSide>
                </ItemContent>
            </ItemCard>
        );
    };

    return (
        <Drawer
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon type="thunderbolt" style={{ color: '#52c41a', fontSize: 18 }} />
                    <span style={{ fontWeight: 600 }}>Conciliação Automática</span>
                </div>
            }
            placement="right"
            closable={true}
            onClose={onClose}
            visible={visible}
            width={900}
            bodyStyle={{ paddingBottom: 80, background: '#f5f5f5' }}
        >
            <HeaderCard>
                <HeaderTitle>
                    <Icon type="check-circle" style={{ color: '#52c41a', fontSize: 20 }} />
                    <div>
                        <HeaderTitleText>Correspondências Encontradas</HeaderTitleText>
                        <HeaderSubtitle>Valores exatamente iguais aos registros do sistema</HeaderSubtitle>
                    </div>
                </HeaderTitle>

                <StatsRow>
                    <StatItem>
                        <StatValue color="#1890ff">{activeItems.length}</StatValue>
                        <StatLabel>
                            Itens
                            {excludedIds.length > 0 && (
                                <span style={{ color: '#ff4d4f' }}> ({excludedIds.length} removido{excludedIds.length > 1 ? 's' : ''})</span>
                            )}
                        </StatLabel>
                    </StatItem>
                    <StatItem>
                        <StatValue color="#52c41a">{toBRL(totalAmount)}</StatValue>
                        <StatLabel>Valor Total</StatLabel>
                    </StatItem>
                    {itemsWithWarning > 0 && (
                        <StatItem>
                            <StatValue color="#faad14">{itemsWithWarning}</StatValue>
                            <StatLabel>Com Alerta</StatLabel>
                        </StatItem>
                    )}
                </StatsRow>
            </HeaderCard>

            <ListContainer>
                {activeItems.length === 0 ? (
                    <EmptyState>
                        <EmptyIcon>
                            <Icon type="inbox" />
                        </EmptyIcon>
                        <EmptyTitle>
                            {matchingItems.length > 0
                                ? 'Todos os itens foram removidos'
                                : 'Nenhuma correspondência encontrada'
                            }
                        </EmptyTitle>
                        <EmptyDescription>
                            {matchingItems.length > 0
                                ? 'Feche a janela para cancelar ou reabra para restaurar os itens.'
                                : 'Não há lançamentos pendentes com valores exatamente iguais no sistema.'
                            }
                        </EmptyDescription>
                    </EmptyState>
                ) : (
                    activeItems.map(renderItem)
                )}
            </ListContainer>

            <DrawerFooter>
                <FooterInfo>
                    {activeItems.length > 0 && (
                        <span>
                            <Icon type="info-circle" style={{ marginRight: 4 }} />
                            Clique em "Remover" para excluir itens da conciliação
                        </span>
                    )}
                </FooterInfo>
                <FooterActions>
                    <Button onClick={onClose} size="large">
                        Cancelar
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleConfirm}
                        loading={isLoading}
                        disabled={activeItems.length === 0}
                        size="large"
                        style={{ background: '#52c41a', borderColor: '#52c41a' }}
                    >
                        <Icon type="check" /> Confirmar ({activeItems.length})
                    </Button>
                </FooterActions>
            </DrawerFooter>
        </Drawer>
    );
};

export default AutoReconcileDrawer;
